import React from 'react';
import { Box, Paper, Typography, Skeleton } from '@mui/material';
import { formatCurrency, formatNumber, formatPercentChange, getChangeColor } from '../../utils/dashboardHelpers';

/**
 * Tarjeta de métrica reutilizable para el dashboard
 * Muestra un valor principal, título, cambio porcentual y comparación con periodo anterior
 */
const MetricCard = ({
  title,
  value,
  change,
  icon,
  type = 'number',
  currency = null,
  loading = false
}) => {
  const formatValue = (val) => {
    if (type === 'currency') return formatCurrency(val, currency);
    if (type === 'percent') return `${parseFloat(val || 0).toFixed(1)}%`;
    return formatNumber(val);
  };

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          height: '100%',
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          transition: 'all 0.3s ease'
        }}
      >
        <Skeleton variant="text" width="60%" height={30} />
        <Skeleton variant="text" width="80%" height={50} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="40%" height={25} sx={{ mt: 1 }} />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        cursor: 'default',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          border: '1px solid rgba(26, 35, 126, 0.2)',
        }
      }}
    >
      {/* Encabezado con icono y título */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon && (
          <Box
            sx={{
              mr: 1.5,
              fontSize: '1.8rem',
              lineHeight: 1
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="subtitle2"
          sx={{
            color: '#666',
            fontWeight: 500,
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Valor principal */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: '#1A237E',
          mb: 1.5,
          fontSize: { xs: '1.75rem', sm: '2rem' }
        }}
      >
        {formatValue(value)}
      </Typography>

      {/* Indicador de cambio */}
      {change !== undefined && change !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: getChangeColor(change),
              fontWeight: 600,
              fontSize: '0.875rem'
            }}
          >
            {formatPercentChange(change)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#999',
              fontSize: '0.75rem'
            }}
          >
            vs periodo anterior
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default MetricCard;
